import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, Send, CheckCircle, Clock, Eye, X, FileText, User, MapPin, MessageSquare, ExternalLink, Scan, Upload, Calendar, ChevronDown, ChevronUp, Box } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const ReturnRequestsView = () => {
  const navigate = useNavigate();
  const [returnRequests, setReturnRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [labelData, setLabelData] = useState({ tracking_number: '', label_url: '', admin_notes: '' });
  const [labelFile, setLabelFile] = useState(null);
  const [additionalFile, setAdditionalFile] = useState(null);
  const fileInputRef = useRef(null);
  const additionalFileRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [expandedPacks, setExpandedPacks] = useState({});

  useEffect(() => {
    fetchReturnRequests();
  }, []);

  const fetchReturnRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/return-requests`, { withCredentials: true });
      setReturnRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch return requests:', error);
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const openLabelModal = (request) => {
    setSelectedRequest(request);
    setLabelData({ tracking_number: '', label_url: '', admin_notes: '' });
    setLabelFile(null);
    setAdditionalFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (additionalFileRef.current) additionalFileRef.current.value = '';
    setShowLabelModal(true);
  };

  const sendLabel = async () => {
    if (!labelData.tracking_number) {
      toast.error('Veuillez remplir le numéro de suivi');
      return;
    }
    if (!labelData.label_url && !labelFile) {
      toast.error('Veuillez fournir l\'URL du bordereau ou uploader un fichier PDF');
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('tracking_number', labelData.tracking_number);
      formData.append('label_url', labelData.label_url || '');
      formData.append('admin_notes', labelData.admin_notes || '');
      if (labelFile) {
        formData.append('label_file', labelFile);
      }
      if (additionalFile) {
        formData.append('additional_file', additionalFile);
      }

      await axios.post(
        `${API}/admin/return-requests/${selectedRequest.id}/send-label`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Bordereau envoyé au client !');
      setShowLabelModal(false);
      fetchReturnRequests();
    } catch (error) {
      console.error('Failed to send label:', error);
      toast.error('Erreur lors de l\'envoi du bordereau');
    } finally {
      setSending(false);
    }
  };

  const deleteRequest = async (requestId) => {
    if (!window.confirm('Supprimer définitivement cette demande de résiliation ?\n\nLa commande sera CONSERVÉE (non supprimée), son abonnement sera réactivé, et elle disparaîtra de cet onglet. Vous pourrez ensuite la gérer normalement dans l\'onglet Commandes.\n\nCette action ne peut pas être annulée.')) return;

    try {
      await axios.delete(
        `${API}/admin/return-requests/${requestId}`,
        { withCredentials: true }
      );
      toast.success('Demande de résiliation supprimée — la commande a été conservée');
      fetchReturnRequests();
    } catch (error) {
      console.error('Failed to delete return request:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const completeReturn = async (requestId) => {
    if (!confirm('Confirmer que le matériel a été reçu et contrôlé ?')) return;

    try {
      await axios.post(
        `${API}/admin/return-requests/${requestId}/complete`,
        {},
        { withCredentials: true }
      );
      toast.success('Retour marqué comme terminé');
      fetchReturnRequests();
    } catch (error) {
      console.error('Failed to complete return:', error);
      toast.error('Erreur lors de la finalisation');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: Clock, text: 'En attente', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
      label_sent: { icon: Truck, text: 'Bordereau envoyé', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      completed: { icon: CheckCircle, text: 'Terminé', color: 'bg-green-500/20 text-green-400 border-green-500/30' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-[#DFFF00] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Demandes de retour</h2>
          <p className="text-slate-400 text-sm mt-1">{returnRequests.length} demande(s)</p>
        </div>
      </div>

      {returnRequests.length === 0 ? (
        <div className="bg-slate-800 rounded-xl p-12 text-center border border-slate-700">
          <Package className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Aucune demande de retour</h3>
          <p className="text-slate-400">Les demandes de résiliation apparaîtront ici</p>
        </div>
      ) : (
        <div className="space-y-4">
          {returnRequests.map((request) => (
            <div
              key={request.id}
              className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden"
              data-testid={`return-request-${request.id}`}
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{request.pack_name}</h3>
                    <p className="text-slate-400 text-sm">
                      {request.customer?.name || 'Client'} • {request.customer?.email}
                    </p>
                  </div>
                </div>
                {getStatusBadge(request.status)}
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="grid md:grid-cols-4 gap-6">
                  {/* Info */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-slate-400 text-sm">
                      <User className="w-4 h-4" />
                      Client
                    </div>
                    <div className="text-white">
                      <p className="font-medium">{request.customer?.name}</p>
                      <p className="text-sm text-slate-400">{request.customer?.company}</p>
                      <p className="text-sm text-slate-400">{request.customer?.phone}</p>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-slate-400 text-sm">
                      <MapPin className="w-4 h-4" />
                      Adresse d'enlèvement
                    </div>
                    <div className="text-white text-sm">
                      {request.pickup_address ? (
                        <>
                          <p className="font-medium">{request.pickup_address.street}</p>
                          {request.pickup_address.complement && <p className="text-slate-400">{request.pickup_address.complement}</p>}
                          <p>{request.pickup_address.zip} {request.pickup_address.city}</p>
                          {request.pickup_address.phone && <p className="text-slate-400">Tel: {request.pickup_address.phone}</p>}
                        </>
                      ) : request.customer?.address ? (
                        <>
                          <p className="font-medium">{request.customer.address}</p>
                          <p>{request.customer.city}</p>
                          {request.customer.phone && <p className="text-slate-400">Tel: {request.customer.phone}</p>}
                        </>
                      ) : (
                        <p className="text-slate-500 italic">Non renseignée</p>
                      )}
                      <p className="text-slate-500 text-xs mt-1">Dept. {request.department} → Marignane (13)</p>
                      {request.weight_kg > 0 && (
                        <p className="text-[#DFFF00] text-sm mt-1 font-bold">Poids total : {request.weight_kg} kg</p>
                      )}
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-slate-400 text-sm">
                      <Clock className="w-4 h-4" />
                      Dates
                    </div>
                    <div className="text-white">
                      <p>Demande : <span className="font-medium">{formatDate(request.created_at)}</span></p>
                      <p>Effet : <span className="font-medium text-orange-400">{formatDate(request.effective_date)}</span></p>
                      {request.pickup_date && (
                        <p className="mt-1">Enlèvement : <span className="font-medium text-[#DFFF00]">
                          {new Date(request.pickup_date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </span></p>
                      )}
                    </div>
                  </div>

                  {/* Payment */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-slate-400 text-sm">
                      <FileText className="w-4 h-4" />
                      Paiement
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#DFFF00]">{request.return_fee?.toFixed(2)}€</p>
                      <p className="text-sm text-green-400">✓ Payé</p>
                    </div>
                  </div>
                </div>

                {/* Reason & Message */}
                <div className="mt-6 pt-6 border-t border-slate-700 grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-2">
                      <MessageSquare className="w-4 h-4" />
                      Motif de résiliation
                    </div>
                    <p className="text-white">{request.reason}</p>
                  </div>
                  {request.message && (
                    <div>
                      <div className="text-slate-400 text-sm mb-2">Message du client</div>
                      <p className="text-slate-300 italic text-sm">"{request.message}"</p>
                    </div>
                  )}
                </div>

                {/* Pack Articles */}
                {(request.pack_components?.length > 0 || request.assigned_items?.length > 0) && (
                  <div className="mt-6 pt-6 border-t border-slate-700">
                    <button
                      onClick={() => setExpandedPacks(prev => ({ ...prev, [request.id]: !prev[request.id] }))}
                      className="flex items-center justify-between w-full text-left group"
                      data-testid={`toggle-pack-articles-${request.id}`}
                    >
                      <div className="flex items-center gap-2 text-slate-400 text-sm">
                        <Box className="w-4 h-4" />
                        <span className="font-medium">Articles du pack</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-300">
                          {request.assigned_items?.length || 0} article{(request.assigned_items?.length || 0) > 1 ? 's' : ''} assigné{(request.assigned_items?.length || 0) > 1 ? 's' : ''}
                        </span>
                      </div>
                      {expandedPacks[request.id] ? (
                        <ChevronUp className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
                      )}
                    </button>
                    
                    {expandedPacks[request.id] && (
                      <div className="mt-3 space-y-2">
                        {(() => {
                          // If assigned items exist, group them by product
                          const items = request.assigned_items || [];
                          if (items.length > 0) {
                            const grouped = {};
                            items.forEach(item => {
                              const pid = item.product_id || 'unknown';
                              if (!grouped[pid]) {
                                grouped[pid] = { product_name: item.product_name || 'Article', count: 0, serials: [] };
                                // Check if this product is in an alternative group
                                const comp = (request.pack_components || []).find(c => c.product_id === pid);
                                if (comp) grouped[pid].is_alternative = comp.is_alternative;
                              }
                              grouped[pid].count++;
                              grouped[pid].serials.push(item.serial_number);
                            });
                            return Object.values(grouped).map((group, idx) => (
                              <div key={idx} className="bg-slate-800/50 rounded-lg p-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Package className="w-3.5 h-3.5 text-[#DFFF00]" />
                                    <span className="text-white text-sm">{group.product_name}</span>
                                    {group.is_alternative && (
                                      <span className="text-xs text-orange-400 px-1.5 py-0.5 rounded bg-orange-500/10">Alternatif</span>
                                    )}
                                  </div>
                                  <span className="text-[#DFFF00] font-semibold text-sm">x{group.count}</span>
                                </div>
                                <div className="mt-1.5 flex flex-wrap gap-1">
                                  {group.serials.map(sn => (
                                    <span key={sn} className="text-xs font-mono text-slate-500 bg-slate-700/50 px-1.5 py-0.5 rounded">{sn}</span>
                                  ))}
                                </div>
                              </div>
                            ));
                          }
                          // Fallback: show pack components template
                          return (request.pack_components || []).map((comp, idx) => (
                            <div key={idx} className="bg-slate-800/50 rounded-lg p-3 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Package className="w-3.5 h-3.5 text-[#DFFF00]" />
                                <span className="text-white text-sm">{comp.product_name}</span>
                                {comp.is_alternative && (
                                  <span className="text-xs text-orange-400 px-1.5 py-0.5 rounded bg-orange-500/10">Alternatif</span>
                                )}
                              </div>
                              <span className="text-[#DFFF00] font-semibold text-sm">x{comp.quantity}</span>
                            </div>
                          ));
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {/* Tracking Info (if label sent) */}
                {request.tracking_number && (
                  <div className="mt-6 pt-6 border-t border-slate-700">
                    <div className="bg-blue-500/10 rounded-lg p-4 flex items-center justify-between">
                      <div>
                        <p className="text-blue-300 text-sm">Numéro de suivi</p>
                        <p className="text-white font-mono text-lg">{request.tracking_number}</p>
                      </div>
                      {request.return_label_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(request.return_label_url, '_blank')}
                          className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Voir bordereau
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-6 pt-6 border-t border-slate-700 flex justify-end gap-3">
                  {request.status !== 'completed' && (
                    <Button
                      onClick={() => deleteRequest(request.id)}
                      variant="outline"
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10 mr-auto"
                      data-testid="delete-return-request-btn"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Supprimer la demande
                    </Button>
                  )}
                  {request.status === 'pending' && (
                    <Button
                      onClick={() => openLabelModal(request)}
                      className="bg-[#DFFF00] hover:bg-[#c8e600] text-black"
                      data-testid="send-label-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer le bordereau
                    </Button>
                  )}
                  {request.status === 'label_sent' && (
                    <>
                      <Button
                        onClick={() => navigate(`/admin/returns/${request.id}/process`)}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        data-testid="process-return-btn"
                      >
                        <Scan className="w-4 h-4 mr-2" />
                        Réceptionner le retour
                      </Button>
                      <Button
                        onClick={() => completeReturn(request.id)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid="complete-return-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Marquer comme terminé
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Send Label Modal */}
      {showLabelModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-2xl max-w-lg w-full border border-slate-700">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white">Envoyer le bordereau de retour</h3>
                <p className="text-slate-400 text-sm mt-1">{selectedRequest.customer?.name} - {selectedRequest.pack_name}</p>
              </div>
              <button
                onClick={() => setShowLabelModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-white font-medium mb-2">Numéro de suivi *</label>
                <input
                  type="text"
                  value={labelData.tracking_number}
                  onChange={(e) => setLabelData({ ...labelData, tracking_number: e.target.value })}
                  placeholder="Ex: 6P12345678901"
                  className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white focus:border-[#DFFF00] focus:outline-none"
                  data-testid="tracking-number-input"
                />
              </div>

              {/* Separator - at least one of URL or File required */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                <p className="text-slate-400 text-xs mb-3">Renseignez l'URL <strong className="text-white">OU</strong> uploadez le fichier PDF (au moins l'un des deux)</p>

                <div className="space-y-3">
                  <div>
                    <label className="block text-white font-medium mb-1.5 text-sm">
                      URL du bordereau PDF {!labelFile && <span className="text-red-400">*</span>}
                    </label>
                    <input
                      type="url"
                      value={labelData.label_url}
                      onChange={(e) => setLabelData({ ...labelData, label_url: e.target.value })}
                      placeholder="https://..."
                      disabled={!!labelFile}
                      className={`w-full bg-slate-800 border rounded-xl px-4 py-2.5 text-white focus:border-[#DFFF00] focus:outline-none text-sm ${labelFile ? 'border-slate-700 opacity-50 cursor-not-allowed' : 'border-slate-600'}`}
                      data-testid="label-url-input"
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-slate-700"></div>
                    <span className="text-slate-500 text-xs font-medium">OU</span>
                    <div className="flex-1 h-px bg-slate-700"></div>
                  </div>

                  <div>
                    <label className="block text-white font-medium mb-1.5 text-sm">
                      Fichier PDF {!labelData.label_url && <span className="text-red-400">*</span>}
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setLabelFile(file);
                            setLabelData(prev => ({ ...prev, label_url: '' }));
                          }
                        }}
                        className="hidden"
                        data-testid="label-file-input"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={!!labelData.label_url}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                          labelData.label_url
                            ? 'border-slate-700 text-slate-500 cursor-not-allowed'
                            : labelFile
                              ? 'border-green-500/50 bg-green-500/10 text-green-400'
                              : 'border-slate-600 text-slate-300 hover:border-[#DFFF00] hover:text-white'
                        }`}
                      >
                        <Upload className="w-4 h-4" />
                        {labelFile ? labelFile.name : 'Choisir un fichier PDF'}
                      </button>
                      {labelFile && (
                        <button
                          type="button"
                          onClick={() => {
                            setLabelFile(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          className="text-red-400 hover:text-red-300 p-1"
                          data-testid="remove-file-btn"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional document */}
              <div>
                <label className="block text-white font-medium mb-1.5 text-sm">Document complémentaire (optionnel)</label>
                <p className="text-slate-500 text-xs mb-2">Confirmation, procès-verbal, ou tout autre document à joindre à l'email</p>
                <div className="flex items-center gap-3">
                  <input
                    ref={additionalFileRef}
                    type="file"
                    accept=".pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setAdditionalFile(file);
                    }}
                    className="hidden"
                    data-testid="additional-file-input"
                  />
                  <button
                    type="button"
                    onClick={() => additionalFileRef.current?.click()}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                      additionalFile
                        ? 'border-green-500/50 bg-green-500/10 text-green-400'
                        : 'border-slate-600 text-slate-300 hover:border-[#DFFF00] hover:text-white'
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    {additionalFile ? additionalFile.name : 'Ajouter un PDF'}
                  </button>
                  {additionalFile && (
                    <button
                      type="button"
                      onClick={() => {
                        setAdditionalFile(null);
                        if (additionalFileRef.current) additionalFileRef.current.value = '';
                      }}
                      className="text-red-400 hover:text-red-300 p-1"
                      data-testid="remove-additional-file-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Notes internes (optionnel)</label>
                <textarea
                  value={labelData.admin_notes}
                  onChange={(e) => setLabelData({ ...labelData, admin_notes: e.target.value })}
                  placeholder="Notes pour l'équipe..."
                  rows={2}
                  className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white focus:border-[#DFFF00] focus:outline-none resize-none"
                />
              </div>

              {/* Pickup info summary */}
              {selectedRequest?.pickup_date && (
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-orange-400 text-sm font-medium mb-1">
                    <Calendar className="w-4 h-4" />
                    Date d'enlèvement prévue
                  </div>
                  <p className="text-white text-sm">
                    {new Date(selectedRequest.pickup_date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
              )}

              <div className="bg-slate-800 rounded-lg p-4">
                <p className="text-slate-400 text-sm">
                  <strong className="text-white">Adresse de retour :</strong><br />
                  PHOCEA RENT - KF DIFFUSION<br />
                  65 Avenue Jacqueline Auriol, Bâtiment 5<br />
                  13700 Marignane
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-slate-700 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowLabelModal(false)}
                className="border-slate-600 text-slate-300"
              >
                Annuler
              </Button>
              <Button
                onClick={sendLabel}
                disabled={sending || !labelData.tracking_number || (!labelData.label_url && !labelFile)}
                className="bg-[#DFFF00] hover:bg-[#c8e600] text-black"
                data-testid="confirm-send-label-btn"
              >
                {sending ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full mr-2"></div>
                    Envoi...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer au client
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReturnRequestsView;
